/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {AuthService} from '../_services/services';
import {Observable} from 'rxjs';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {BOVICLIC_JWT_KEY, BOVICLIC_PATH, GRANT_TYPE, GT_REFRESH_TOKEN, REFRESH_TOKEN} from '../_enums/constants';
import {ToolsHelper} from '../_helpers/tools-helper';
import {catchError, mergeMap} from 'rxjs/operators';
import {Token} from '../_models/token';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGIC FUNCTIONS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(public authService: AuthService, public router: Router) {
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              STATIC METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Check if referer is a boviclic/Synel/Leo page
   *
   * @param url string
   *
   * @return boolean
   */
  private static isBoviclicReferer(url?: string): boolean {
    return url ? url.startsWith(BOVICLIC_PATH, 0) : false;
  }

  private static isExternalReferer(url?: string): boolean {
    return url ? url.startsWith('/external-removal') : false;
  }

  /**
   * Check if request is to refresh the token
   *
   * @param request the intercepted request
   */
  private static isRefreshTokenRequest(request: HttpRequest<any>): boolean {
    return TokenInterceptor.isTokenRequest(request.url) && request.params &&
      request.params.get(GRANT_TYPE) === GT_REFRESH_TOKEN && !ToolsHelper.isUndefinedOrNull(request.params.get(REFRESH_TOKEN));
  }

  /**
   * Check if request is to handle token
   *
   * @param url string
   *
   * @return boolean
   */
  private static isTokenRequest(url?: string): boolean {
    return url ? url.endsWith(environment.oAuth.urlToken) : false;
  }

  /**
   * Update request auth headers
   *
   * @param request the intercepted request
   * @param token Bearer token
   */
  private static setAuth(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              OVERRIDE METHODS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const coa = this.authService.getCurrentToken();

    if (TokenInterceptor.isBoviclicReferer(this.router.url) ||TokenInterceptor.isExternalReferer(this.router.url)) {
      const jwtAuthToken = AuthService.getAuthToken(BOVICLIC_JWT_KEY);
      return next.handle(jwtAuthToken ? TokenInterceptor.setAuth(request, jwtAuthToken) : request);
    } else if (coa) {
      if (!coa.isExpired) {
        return next.handle(TokenInterceptor.setAuth(request, coa.accessToken));
      } else if (!TokenInterceptor.isRefreshTokenRequest(request)) {
        return this.authService.refreshToken().pipe(
          mergeMap((newToken: Token) => {
            return next.handle(TokenInterceptor.setAuth(request, newToken.accessToken));
          }),
          catchError(() => {
            return next.handle(request);
          })
        );
      }
    }

    return next.handle(request);
  }

}
